import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { auth } from './firebase';
import { signOut,getAuth } from 'firebase/auth';
import AuthPage from './components/AuthPage';
import ReturnToAppPage from './components/ReturnToAppPage';
import SubscriptionPage from './components/SubscriptionPage';
import MyDesignsPage from './components/MyDesignsPage';
import AccountPage from './components/AccountPage';
import ExternalLinkLandingPage from './components/ExternalLinkLandingPage';
import LoadingOverlay from './components/LoadingOverlay';
import DevDataPage from './components/DevDataPage';
import AffiliateModal from './components/AffiliateModal';
import ProtectedRoute from './components/ProtectedRoute';
import CreditsPage from './pages/CreditsPage';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import config from './config';
import { validateSession } from './utils/authUtils';
import './App.css';

// Move AuthenticatedLayout outside to prevent re-creation
const AuthenticatedLayout = React.memo(({ navigate, location, handleSignOut }) => {
  return (
    <>
      <header className="app-header">
        <div className="header-content">
          <div className="header-left">
            <div className="logo">
              <img src="/images/dream-book-logo-light.png" alt="Dreambook Logo" />
            </div>
          </div>
          <div className="header-right">
            <nav className="main-nav">
              <button 
                className={`nav-link ${location.pathname.startsWith('/') && location.pathname.length === 1 ? 'active' : ''}`}
                onClick={() => navigate('/')}
              >
                Account
              </button>
              <button 
                className={`nav-link ${location.pathname.startsWith('/subscription') ? 'active' : ''}`}
                onClick={() => navigate('/subscription')}
              >
                Pro
              </button>
              <button 
                className={`nav-link ${location.pathname.startsWith('/credits') ? 'active' : ''}`}
                onClick={() => navigate('/credits')}
              >
                Credits
              </button>
              <button 
                className={`nav-link ${location.pathname.startsWith('/my-designs') ? 'active' : ''}`}
                onClick={() => navigate('/my-designs')}
              >
                My Designs
              </button>
              {config.isDevelopment && (
                <button 
                  className={`nav-link ${location.pathname.startsWith('/dev-data') ? 'active' : ''}`}
                  onClick={() => navigate('/dev-data')}
                >
                  Development
                </button>
              )}
            </nav>
          </div>
        </div>
      </header>
      <main className="main-content">
        <Outlet />
      </main>
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-left">
            <span className="footer-text">Copyright 2025 dream book, LLC. All rights reserved.</span>
          </div>
          <div className="footer-right">
            <a href="https://dreambook3d.com/privacy" className="footer-link" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            <a href="https://dreambook3d.com/terms" className="footer-link" target="_blank" rel="noopener noreferrer">Terms of Service</a>
            <button onClick={handleSignOut} className="sign-out-button">
              Sign Out
            </button>
          </div>
        </div>
      </footer>
    </>
  );
});

function App() {
  const [user, setUser] = useState(null);
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unityAuthData, setUnityAuthData] = useState(null);
  const [cloudSaveData, setCloudSaveData] = useState(null);
  const [unityUsername, setUnityUsername] = useState('');
  const [cleanedUsername, setCleanedUsername] = useState('');
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(localStorage.getItem('isRedirecting') === 'true');
  const [canMakePayments, setCanMakePayments] = useState(true);
  const [showAffiliateModal, setShowAffiliateModal] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const API_BASE_URL = config.backendUrl;

  const ShouldRedirect = async (providerId, token) => {
    try {
      // If showing affiliate modal, don't redirect yet
      if (showAffiliateModal) {
        return false;
      }
      
      const searchParams = new URLSearchParams(window.location.search);
      let redirectUrl = searchParams.get('redirect') || localStorage.getItem('redirectUrl');
      
      // Always clear the hasRedirected flag when a new redirect URL is present in query params
      if (searchParams.get('redirect')) {
        sessionStorage.removeItem('hasRedirected');
      }
      
      // Check if we've already redirected for this session
      const hasRedirected = sessionStorage.getItem('hasRedirected');
      
      console.log('should redirect?', redirectUrl, 'already redirected?', hasRedirected);
      // Force redirect when URL is in query params, otherwise check hasRedirected flag
      if (redirectUrl && (searchParams.get('redirect') || !hasRedirected)) {
        setIsRedirecting(true);
        localStorage.setItem('isRedirecting', 'true');
        localStorage.setItem('redirectWithUnityToken', searchParams.get('unityToken')?'true':'false');

        // Mark that we've redirected for this session
        sessionStorage.setItem('hasRedirected', 'true');
        
        let desktopApp = false;//!redirectUrl.includes('//');

        if (searchParams.has('dev') && searchParams.get('dev').toLowerCase() !== 'false') {
          desktopApp = true;
        }
        
        if (desktopApp) {
         
          console.log("Redirecting to ReturnToAppPage for desktop app");
          localStorage.setItem('redirectUrl', redirectUrl);
          localStorage.setItem('authToken', token);
          // Navigate to the ReturnToAppPage which will handle the redirect
          window.location.href = '/return-to-app';
          return true;
        } else {
          console.log('Redirecting to:', `${redirectUrl}/login?${token}`);
          // Clear the redirect URL from localStorage
          localStorage.removeItem('redirectUrl');
          window.location.href = `${redirectUrl}/login?${token}`;
          return true;
        }
      } else {
        localStorage.removeItem('redirectUrl');
        localStorage.removeItem('isRedirecting');
        localStorage.setItem('authProvider', providerId);
        return false;
      }
    } catch (error) {
      console.error('Error after authentication:', error);
      setIsRedirecting(false);
      localStorage.removeItem('isRedirecting');
      return false;
    }
  };

  const getAuthHeaders = async (unityAuthData) => {
    // Get Firebase token
    const auth = getAuth();
    const currentUser = auth.currentUser;
    let firebaseToken = '';
    
    if (currentUser) {
      try {
        firebaseToken = await currentUser.getIdToken();
        console.log('Got Firebase token for authentication');
      } catch (error) {
        console.error('Error getting Firebase token:', error);
      }
    } else {
      console.warn('No Firebase user is logged in');
    }
    
    // Create headers with both tokens
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': firebaseToken ? `Bearer ${firebaseToken}` : '',
      'X-Unity-Token': unityAuthData?.accessToken || ''
    };
    
    return headers;
  };

  const fetchCloudSaveData = async (unityAuthData, currentUser, forceRefresh = false, retryCount = 0) => {
    // Prevent infinite retry loops
    if (retryCount > 2) {
      console.error('Too many retries when fetching cloud save data');
      return null;
    }
    
    if (cloudSaveData && !forceRefresh) {
      console.log('Using cached cloud save data');
      return cloudSaveData;
    }
    
    try {
      // Check if playerId exists before proceeding
      if (!unityAuthData?.playerId) {
        console.warn('Cannot fetch cloud save data: Missing player ID', { unityAuthData });
        return null; // Return null instead of throwing to allow sign-in flow to complete
      }
      
      console.log('Fetching cloud save data...', { 
        playerId: unityAuthData.playerId,
        forceRefresh,
        hasCurrentToken: !!currentUser
      });
      
      // Ensure we have a fresh token
      let freshToken = '';
      if (currentUser) {
        try {
          freshToken = await currentUser.getIdToken(true);
          console.log('Got fresh token for cloud save request');
        } catch (tokenError) {
          console.error('Error getting fresh token:', tokenError);
          // Don't sign out here as it prevents sign-in
        }
      } else {
        // Don't sign out when no current user to allow sign-in flow
        console.warn('No current user available for authenticated request');
        // Only throw error if we're on a protected page
        if (!['/external-link', '/share-design', '/auth', '/return-to-app', '/sign-in', '/create-account'].includes(window.location.pathname)) {
          throw new Error('No authenticated user available for request');
        }
      }
      
      // Only include standard headers, no cache-control to avoid CORS issues
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': freshToken ? `Bearer ${freshToken}` : '',
        'X-Unity-Token': unityAuthData?.accessToken || ''
      };
      
      // Add cache buster to URL
      const cacheBusterUrl = `${API_BASE_URL}/cloud-save/${unityAuthData.playerId}?_t=${Date.now()}`;
      console.log('Making cloud save request to:', cacheBusterUrl);
      
      const response = await fetch(cacheBusterUrl, { 
        headers
      });
      
      if (!response.ok) {
        console.error('Cloud save API failed with status:', response.status);
        
        // Check for Unity token expiration (500 Internal Server Error might indicate expired token)
        if ((response.status === 500 || response.status === 401) && currentUser && retryCount < 2) {
          console.log('Possible Unity token expiration detected, attempting to refresh Unity token...');
          try {
            // Re-authenticate with Unity to get a fresh token
            const token = await currentUser.getIdToken(true);
            const newUnityData = await authenticateWithUnity(token);
            
            // Update unity auth data in state and localStorage
            localStorage.setItem('unityAuthData', JSON.stringify(newUnityData));
            setUnityAuthData(newUnityData);
            
            console.log('Successfully refreshed Unity token, retrying cloud save request');
            // Retry the request with the new token
            return await fetchCloudSaveData(newUnityData, currentUser, true, retryCount + 1);
          } catch (refreshError) {
            console.error('Failed to refresh Unity token:', refreshError);
          }
        }
        
        if (response.status === 401 || response.status === 403) {
          console.error('Authentication error when fetching cloud data, signing out user');
          // Sign out due to auth failure - only on protected routes
          if (!['/external-link', '/share-design', '/auth', '/return-to-app', '/sign-in', '/create-account'].includes(window.location.pathname)) {
            await handleSignOut(false);
          }
          throw new Error(`Failed to fetch cloud save data: ${await response.text()}`);
        }
        throw new Error(`Failed to fetch cloud save data: ${await response.text()}`);
      }

      const data = await response.json();
      console.log('Cloud save data received:', data);
      
      
      // Check if the response is incomplete (just username with no results)
      const isIncompleteResponse = (!data.results || data.results.length === 0) && data.username;
      const isEmptyResponse = (!data.results || data.results.length === 0) && !data.username;

      // Verify we have valid data before using it
      if (!data || (isIncompleteResponse && cloudSaveData)) {
        console.warn('Received incomplete cloud save data response but have existing data, keeping existing data');
        return cloudSaveData;
      }
      
      // Special handling for incomplete but not empty responses
       if (isEmptyResponse && !cloudSaveData && currentUser && retryCount < 2) {
         console.log('Received incomplete data, might be due to expired Unity token. Attempting refresh...');
        try {
          // Re-authenticate with Unity to get a fresh token
          const token = await currentUser.getIdToken(true);
          console.log("Refresh Unity token from fetchCloudSaveData 2");

          const newUnityData = await authenticateWithUnity(token);

          // Update unity auth data in state and localStorage
          localStorage.setItem('unityAuthData', JSON.stringify(newUnityData));
          setUnityAuthData(newUnityData);

          console.log('Successfully refreshed Unity token after incomplete data, retrying request');
          // Retry the request with the new token
          return await fetchCloudSaveData(newUnityData, currentUser, true, retryCount + 1);
        } catch (refreshError) {
          console.error('Failed to refresh Unity token after incomplete data:', refreshError);
        }
      }
      
      const needsSetup = isIncompleteResponse;
      const accountType = data.results?.find(item => item.key === "accountType")?.value;
      
      if ((needsSetup || accountType === undefined || accountType === 0) && !forceRefresh) {
        console.log('First time setup required');
        const setupResult = await firstTimeSetup(unityAuthData, currentUser);
        if (setupResult) {
          return await fetchCloudSaveData(unityAuthData, currentUser, true, retryCount + 1);
        }
      }
      
      // Only update state if we have a valid response with results
    if (data.results && !data.username) {
        console.log('Setting cloud save data state:', data);
        setCloudSaveData(data);
        
        // Make sure username gets set immediately
        if (data.username) {
          console.log('Setting username from cloud save data:', data.username);
          setUnityUsername(data.username);
          setCleanedUsername(data.username.split('#')[0]);
        }
      } else if (cloudSaveData) {
        console.warn('New data is incomplete, keeping existing cloud save data');
        return cloudSaveData;
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching cloud save data:', error);
      
      // Only sign out on network errors when on a protected page
      if ((error.message === 'Failed to fetch' || error.name === 'TypeError') && 
          !['/external-link', '/share-design', '/auth', '/return-to-app', '/sign-in', '/create-account'].includes(window.location.pathname)) {
        console.error('Network error when fetching cloud data on protected page. Signing out user.');
        handleSignOut(false);
      }
      
      throw error;
    }
  };

  const firstTimeSetup = async (unityAuthData, currentUser, retryCount = 0) => {
    // Prevent infinite retry loops
    if (retryCount > 2) {
      console.error('Too many retries when setting up user data');
      return false;
    }
    
    try {
      console.log('Performing first time setup...');
      
      // Ensure we have fresh tokens
      if (!unityAuthData?.accessToken && currentUser && retryCount < 2) {
        console.log('Missing or invalid Unity token for setup, attempting to refresh...');
        try {
          const token = await currentUser.getIdToken(true);
          const newUnityData = await authenticateWithUnity(token);
          
          // Update unity auth data in state and localStorage
          localStorage.setItem('unityAuthData', JSON.stringify(newUnityData));
          setUnityAuthData(newUnityData);
          
          // Use the new Unity data for setup
          return await firstTimeSetup(newUnityData, currentUser, retryCount + 1);
        } catch (refreshError) {
          console.error('Failed to refresh Unity token for setup:', refreshError);
        }
      }
      
      const headers = await getAuthHeaders(unityAuthData);
      
      const username = currentUser?.displayName || currentUser?.email?.split('@')[0] || `Player${Math.floor(Math.random() * 10000)}`;
      const providerId = currentUser?.providerData?.[0]?.providerId || 'unknown';
      
      const response = await fetch(`${API_BASE_URL}/cloud-save/${unityAuthData?.playerId}/setup`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ username, provider: providerId }),
      });
      
      if (!response.ok) {
        // Handle token expiration in setup
        if ((response.status === 500 || response.status === 401) && currentUser && retryCount < 2) {
          console.log('Possible Unity token expiration during setup, attempting to refresh...');
          try {
            const token = await currentUser.getIdToken(true);
            const newUnityData = await authenticateWithUnity(token);
            
            // Update unity auth data in state and localStorage
            localStorage.setItem('unityAuthData', JSON.stringify(newUnityData));
            setUnityAuthData(newUnityData);
            
            // Retry setup with new token
            return await firstTimeSetup(newUnityData, currentUser, retryCount + 1);
          } catch (refreshError) {
            console.error('Failed to refresh Unity token during setup:', refreshError);
          }
        }
        throw new Error(`Failed to set initial user data: ${await response.text()}`);
      }
      
      console.log('Initial user data set successfully');
      return true;
    } catch (error) {
      console.error('Error setting initial user data:', error);
      return false;
    }
  };

  const authenticateWithUnity = async (idToken) => {
    console.log('Authenticating with Unity...');
    try {

      const urlParams = new URLSearchParams(window.location.search);
      const linkParam = urlParams.get('link');
      
      // Determine endpoint and headers
      const endpoint = linkParam ? 'auth/unity-link' : 'auth/unity';
      const headers = linkParam
          ? {
            'X-Unity-Token': linkParam,
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
            }
          : {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${idToken}`
          };
      
      const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ idToken}),
        mode: 'cors'
      });
      
      if (!response.ok)
        throw new Error(`Unity authentication failed: ${await response.text()}`);
      const unityData = await response.json();
      console.log('Unity authentication successful:', unityData);
      
      // If we used the link parameter, remove it from the URL to prevent duplicate calls
      if (linkParam) {
        // Create new URL params without the link parameter
        const newUrlParams = new URLSearchParams(window.location.search);
        newUrlParams.delete('link');
        
        // Update the URL without causing a page reload
        const newUrl = newUrlParams.toString() 
          ? `${window.location.pathname}?${newUrlParams.toString()}` 
          : window.location.pathname;
          
        window.history.replaceState({}, document.title, newUrl);
        console.log('Removed link parameter from URL after successful authentication');
      }
      
      if (!unityData.playerId || !unityData.accessToken) {
        throw new Error('Missing player ID or access token from Unity response');
      }
      
      return unityData;
    } catch (error) {
      console.error('Unity authentication error:', error);
      throw error;
    }
  };
  
  const cleanupUserData = () => {
    // Clear all local storage to prevent data leaks
    localStorage.clear();
    
    // Reset all user-related state
    setUser(null);
    setProvider(null);
    setUnityAuthData(null);
    setCloudSaveData(null);
    setUnityUsername('');
    setCleanedUsername('');
    setIsEditingUsername(false);
    setIsRedirecting(false);
    setError(null);
    setCanMakePayments(true); // Reset payment capability to default state
    setLoading(false);
    
    // Navigate to auth page
    navigate('/auth');
  };

  const handleSignOut = async () => {
    console.log('Signing out user...');
    try {
      await signOut(auth);
      cleanupUserData();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSaveUsername = async () => {
    try {
      if (!cleanedUsername) return;
      
      const headers = await getAuthHeaders(unityAuthData);
      const response = await fetch(`${API_BASE_URL}/cloud-save/${unityAuthData?.playerId}/username`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ username: cleanedUsername }),
      });

      if (!response.ok) throw new Error(`Failed to update username: ${await response.text()}`);
      
      setUnityUsername(cleanedUsername);
      setIsEditingUsername(false);
      
      // Update cloud save data to reflect the change
      const updatedCloudData = { ...cloudSaveData, username: cleanedUsername };
      setCloudSaveData(updatedCloudData);
    } catch (error) {
      console.error('Error saving username:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleEditUsername = useCallback((value) => {
    setCleanedUsername(value);
  }, []);

  // Updated affiliate modal close handler that respects tracking completion
  const handleAffiliateModalClose = (trackingSuccessful) => {
    console.log('Closing affiliate modal, tracking successful:', trackingSuccessful);
    
    // Hide the modal
    setShowAffiliateModal(false);
    
    // Continue with redirect if needed
    if (user && user.providerData?.[0]) {
      const providerId = user.providerData[0].providerId;
      user.getIdToken().then(token => {
        // Use the redirect URL from localStorage if available
        const redirectUrl = localStorage.getItem('redirectUrl');
        if (redirectUrl) {
          console.log('Redirecting after affiliate modal close');
          setIsRedirecting(true);
          localStorage.setItem('isRedirecting', 'true');
          
          // Mark that we've redirected for this session
          sessionStorage.setItem('hasRedirected', 'true');
          
          const desktopApp = false;// !redirectUrl.includes('//');
          
          if (desktopApp) {
            console.log('Redirecting to desktop app...');
            localStorage.setItem('authToken', token);
            window.location.href = '/return-to-app';
          } else {
            console.log('Redirecting to:', `${redirectUrl}/login?${token}`);
            // Clear the redirect URL from localStorage immediately
            localStorage.removeItem('redirectUrl');
            window.location.href = `${redirectUrl}/login?${token}`;
          }
        } else {
          localStorage.removeItem('isRedirecting');
          localStorage.setItem('authProvider', providerId);
        }
      }).catch(error => {
        console.error('Error getting token for redirect:', error);
      });
    }
  };

  // Handler to update cloudSaveData from child components
  const handleCloudSaveUpdate = useCallback((updatedData) => {
    setCloudSaveData(updatedData);
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      console.log('Auth state changed:', currentUser?.uid, window.location.pathname);
      
      // Skip auth handling for external-link page
      if (window.location.pathname === '/external-link' || window.location.pathname === '/share-design') {
        setLoading(false);
        return;
      }

      setLoading(true);
      
      if (!currentUser) {
        console.log('No user logged in');
        setUser(null);
        setProvider(null);
        setUnityAuthData(null);
        setCloudSaveData(null);
        setLoading(false);
        return;
      }
      
      const searchParams = new URLSearchParams(window.location.search);
      const action = searchParams.get('action');
      
      // Only clear payments flag if explicitly enabled in URL
      if (searchParams.has('canMakePayments') && searchParams.get('canMakePayments').toLowerCase() !== 'false') {
        localStorage.removeItem('paymentsDisabled');
      }
      
      // Handle logout action
      if (action === 'logout') {
        console.log('Logout action detected, signing out...');
        await signOut(auth);
        cleanupUserData();
        setLoading(false);
        return;
      }
      
      // Special handling for return-to-app path
      if (window.location.pathname === '/return-to-app') {
        setUser(currentUser);
        setLoading(false);
        return;
      }
      
      try {
        const token = await currentUser.getIdToken();
        let unityData = JSON.parse(localStorage.getItem('unityAuthData'));
        
        // Check if this is a new user (creation time within last 30 seconds)
        const isNewUserSignUp = currentUser.metadata && 
          (new Date().getTime() - new Date(currentUser.metadata.creationTime).getTime() < 30000);
        
        if (isNewUserSignUp) {
          setIsNewUser(true);
        }
        
        if (!unityData) {
          unityData = await authenticateWithUnity(token);
          localStorage.setItem('unityAuthData', JSON.stringify(unityData));
        }
        
        // Batch state updates to minimize renders
        setUser(currentUser);
        setUnityAuthData(unityData);
        
        // Fetch cloud save data
        const cloudData = await fetchCloudSaveData(unityData, currentUser);
        setCloudSaveData(cloudData);
        
        // Handle provider data and redirection
        if (currentUser.providerData?.[0]) {
          setProvider(currentUser.providerData[0]);
          
          // Check if affiliate modal has already been shown in this session
          const hasShownAffiliateModal = sessionStorage.getItem('hasShownAffiliateModal');
          
          // Show affiliate modal for new users before redirecting
          if (isNewUserSignUp && !hasShownAffiliateModal) {
            console.log('New user detected, showing affiliate modal');
            sessionStorage.setItem('hasShownAffiliateModal', 'true');
            setShowAffiliateModal(true);
          } else {
            // Only redirect if not a new user (no affiliate modal needed)
            const searchParams = new URLSearchParams(window.location.search);
            const redirectParam = searchParams.get('redirect');
            const redirectUrl = redirectParam || localStorage.getItem('redirectUrl');
            
            if (redirectParam && !localStorage.getItem('redirectUrl')) {
              localStorage.setItem('redirectUrl', redirectParam);
            }
            
            const useUnityToken = searchParams.get('unityToken') === 'true';
            if (redirectUrl) {
              await ShouldRedirect(currentUser.providerData[0].providerId, useUnityToken ? unityData.accessToken:token);
            }
          }
        }
        
        //await fetchSubscriptionStatus(currentUser);
      } catch (error) {
        console.error('Authentication error:', error);
        setError(error.message);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Skip for public routes
    if (window.location.pathname === '/external-link' || 
        window.location.pathname === '/share-design' || 
        window.location.pathname === '/auth' || 
        window.location.pathname === '/return-to-app') {
      return;
    }

    const checkAuthStatus = async () => {
      if (user) {
        const isSessionValid = await validateSession();
        if (!isSessionValid && !loading) {
          console.log('Session invalid, logging out user');
          handleSignOut(false);
        }
      }
    };

    checkAuthStatus();
  }, [location.pathname]);

  useEffect(() => {
    // Skip for public routes
    if (window.location.pathname === '/external-link' || 
        window.location.pathname === '/share-design' || 
        window.location.pathname === '/auth' || 
        window.location.pathname === '/return-to-app') {
      return;
    }

    const tokenValidationInterval = setInterval(async () => {
      if (user) {
        const isSessionValid = await validateSession();
        if (!isSessionValid && !loading) {
          console.log('Session token expired during interval check, logging out user');
          handleSignOut(false);
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(tokenValidationInterval);
  }, [user, loading]);

  useEffect(() => {
    // Add meta tags to prevent browser caching
    let metaNoCache = document.querySelector('meta[http-equiv="Cache-Control"]');
    if (!metaNoCache) {
      metaNoCache = document.createElement('meta');
      metaNoCache.setAttribute('http-equiv', 'Cache-Control');
      metaNoCache.setAttribute('content', 'no-cache, no-store, must-revalidate');
      document.head.appendChild(metaNoCache);
    }
    
    let metaPragma = document.querySelector('meta[http-equiv="Pragma"]');
    if (!metaPragma) {
      metaPragma = document.createElement('meta');
      metaPragma.setAttribute('http-equiv', 'Pragma');
      metaPragma.setAttribute('content', 'no-cache');
      document.head.appendChild(metaPragma);
    }
    
    let metaExpires = document.querySelector('meta[http-equiv="Expires"]');
    if (!metaExpires) {
      metaExpires = document.createElement('meta');
      metaExpires.setAttribute('http-equiv', 'Expires');
      metaExpires.setAttribute('content', '0');
      document.head.appendChild(metaExpires);
    }

    // Parse query parameters on every page load
    const parsedParams = new URLSearchParams(window.location.search);
    
    // Always check for canMakePayments parameter specifically
    const paymentsParam = parsedParams.get('canMakePayments');
    if (paymentsParam !== null) {
      const paymentsDisabled = paymentsParam.toLowerCase() === 'false';
      console.log(`canMakePayments parameter found: ${!paymentsDisabled}`);
      localStorage.setItem('paymentsDisabled', paymentsDisabled ? 'true' : 'false');
      setCanMakePayments(!paymentsDisabled);
      
      // Force refresh cloud save data when this parameter is present
      // if (user && unityAuthData) {
      //   console.log('canMakePayments parameter detected, forcing cloud save refresh');
      //   fetchCloudSaveData(unityAuthData, user, true)
      //       .then(data => {
      //         console.log('Cloud save refresh successful:', data);
      //         // Ensure username is set
      //         if (data.username) {
      //           setUnityUsername(data.username);
      //           setCleanedUsername(data.username.split('#')[0]);
      //         }
      //       })
      //       .catch(error => {
      //         console.error('Error during forced cloud save refresh:', error);
      //       });
      // }
    }

    console.log('Current app state:', { 
      canMakePayments, 
      user: !!user, 
      unityAuthData: !!unityAuthData,
      username: unityUsername 
    });
  }, [user, unityAuthData]);
  
  // Ensure username is set when cloud save data changes
  useEffect(() => {
    if (cloudSaveData?.username) {
      console.log('Setting username from cloud save data change:', cloudSaveData.username);
      setUnityUsername(cloudSaveData.username);
      setCleanedUsername(cloudSaveData.username.split('#')[0]);
    }
  }, [cloudSaveData]);

  useEffect(() => {
    setIsRedirecting(false);
    localStorage.removeItem('isRedirecting');
  }, [location.pathname, location.search]);

  const memoizedAccountPage = useMemo(() => (
    <AccountPage 
      user={user} 
      unityAuthData={unityAuthData} 
      cloudSaveData={cloudSaveData}
      onEditUsername={handleEditUsername}
      unityUsername={unityUsername}
      cleanedUsername={cleanedUsername}
      isEditingUsername={isEditingUsername}
      setIsEditingUsername={setIsEditingUsername}
      handleSaveUsername={handleSaveUsername}
      canMakePayments={canMakePayments}
    />
  ), [user, unityAuthData, cloudSaveData, unityUsername, cleanedUsername, isEditingUsername, setIsEditingUsername, handleSaveUsername, handleEditUsername]);

  const authenticatedRoutes = (
    <Routes>
      <Route element={<ProtectedRoute><AuthenticatedLayout navigate={navigate} location={location} handleSignOut={handleSignOut} /></ProtectedRoute>}>
        <Route index element={memoizedAccountPage} />
        <Route path="/subscription" element={<SubscriptionPage user={user} unityAuthData={unityAuthData} canMakePayments={canMakePayments} />} />
        <Route path="/my-designs" element={<MyDesignsPage user={user} unityAuthData={unityAuthData} cloudSaveData={cloudSaveData} />} />
        <Route path="/credits" element={<CreditsPage 
          cloudSaveData={cloudSaveData} 
          unityAuthData={unityAuthData} 
          onCloudSaveUpdate={handleCloudSaveUpdate}
        />} />
        {config.isDevelopment && (
          <Route path="/dev-data" element={<DevDataPage user={user} unityAuthData={unityAuthData} cloudSaveData={cloudSaveData} />} />
        )}
      </Route>
    </Routes>
  );

  // Early return for external-link page
  if (window.location.pathname === '/external-link' || window.location.pathname === '/share-design') {
    return (
      <div className="app">
        <Routes>
          <Route path="/external-link" element={<ExternalLinkLandingPage />} />
          <Route path="/share-design" element={<ExternalLinkLandingPage />} />
          <Route path="*" element={<Navigate to="/external-link" />} />
        </Routes>
      </div>
    );
  }

  if (loading) return <div className="app">Loading...</div>;

  return (
    <div className="app">
      {isRedirecting && <LoadingOverlay message="Setting up your account and redirecting..." />}
      {showAffiliateModal && user && unityAuthData && (
        <AffiliateModal 
          userId={unityAuthData.playerId} 
          onClose={handleAffiliateModalClose} 
        />
      )}
      
      {/* Wrap the routes that need subscription data in the SubscriptionProvider */}
      {user && unityAuthData ? (
        <SubscriptionProvider unityAuthData={unityAuthData} cloudSaveData={cloudSaveData}>
          <Routes>
            <Route path="/auth" element={!user ? <AuthPage /> : <Navigate to="/" />} />
            <Route path="/sign-in" element={<Navigate to={`/auth${window.location.search}`} replace />} />
            <Route path="/create-account" element={<Navigate to={`/auth${window.location.search}`} replace />} />
            <Route path="/return-to-app" element={<ReturnToAppPage />} />
            <Route path="/*" element={!user ? <Navigate to={`/auth${window.location.search}`} /> : authenticatedRoutes} />
          </Routes>
        </SubscriptionProvider>
      ) : (
        <Routes>
          <Route path="/auth" element={!user ? <AuthPage /> : <Navigate to="/" />} />
          <Route path="/sign-in" element={<Navigate to={`/auth${window.location.search}`} replace />} />
          <Route path="/create-account" element={<Navigate to={`/auth${window.location.search}`} replace />} />
          <Route path="/return-to-app" element={<ReturnToAppPage />} />
          <Route path="/*" element={!user ? <Navigate to={`/auth${window.location.search}`} /> : authenticatedRoutes} />
        </Routes>
      )}
    </div>
  );
}

export default App;